import React, { useMemo, useEffect } from 'react';
import Emoji from 'react-emoji-render';
import classnames from 'classnames';
import { get } from 'lodash';

import DayJs, { CHAT_CALENDAR } from 'utils/Dayjs';

import Show from 'components/Show';
import Icon from 'components/Misc/Icon';

import { useAppStore } from 'store/useAppStore';
import { useMessengerStore } from 'store/useMessengerStore';

const Message = ({ userId, message, idx }) => {
	// Use app's store
	const [socket, messenger] = useAppStore(state => [state.socket, state.messenger]);

	// Use messenger's store
	const conversation = useMessengerStore(state => state.conversations.find(c => c.id === state.active));

	// Store previous message
	const previous = useMemo(() => get(conversation.messages, [idx - 1]), [idx, conversation]);

	// Store message's meta data flag
	const meta = useMemo(() => ({
		isOwner: userId === message.userId,
		hasRead: !!message.readby.find(r => r.userId === userId),
		allRead: conversation?.participants?.length === message?.readby?.length,
		displayDay: !previous || DayJs(previous.createdAt).isBefore(message.createdAt, 'day'),
		displayTime: get(conversation.messages, [idx + 1])?.userId !== message.userId
	}), [idx, userId, previous, message]);

	// Store message's wrap classNames
	const wrapClassName = classnames(
		'flex flex-col',
		meta.isOwner
			? 'items-end'
			: 'items-start'
	);

	// Store message's container classNames
	const messageClassName = classnames(
		'pt-2 pb-1 px-2 rounded-lg min-w-[20%] max-w-[85%]',
		meta.isOwner
			? 'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-100 rounded-br-none transition-colors'
			: 'border border-neutral-100 dark:border-neutral-700 text-neutral-700 dark:text-neutral-100 rounded-bl-none transition-colors'
	);

	// Emit read event on message mount
	useEffect(() => {
		if (messenger && !meta.isOwner && !meta.hasRead) socket.emit('read', {
			id: message.id
		});
	}, [messenger, meta, socket]);

	return (
		<>
			{/* Display message's date if new date has begun */}
			<Show if={meta.displayDay}>
				<div className="sticky -top-2 flex justify-center">
					<div className="my-2 w-1/3 px-2 font-bold text-sm text-neutral-600 dark:text-neutral-100 rounded-full bg-neutral-100 dark:bg-neutral-700 text-center capitalize transition-colors">{DayJs(message.createdAt).calendar(null, CHAT_CALENDAR)}</div>
				</div>
			</Show>
				
			<div className={wrapClassName}>
				<div className={messageClassName}>
					{/* Message's content */}
					<Emoji
						text={message.message}
						className="block whitespace-pre-wrap break-words text-sm leading-snug"
						onlyEmojiClassName="text-2xl text-center w-full block"
					/>

					{/* Message's data */}
					<div className="flex items-center justify-end space-x-0.5 mt-0.5 leading-none">
						<Show if={meta.isOwner && meta.allRead}>
							<Icon icon="check" className="text-xs text-emerald-500 animate-fade-in" />
						</Show>

						<span className="text-xs text-neutral-500">{DayJs(message.createdAt).format('HH:mm')}</span>
					</div>
				</div>
			</div>
		</>
	);
};

export default Message;